import { isContractImpactingChange } from './missionFieldImpact';

/**
 * What a modification will actually do to the BeSTT contract.
 *
 * The confirmation modal used to hedge — "the contract will be updated, or, if
 * hours have been imported, an amendment will be created". The admin has no way
 * of knowing which, and the two are not the same thing at all: a patch rewrites
 * the contract in place, an amendment adds a document that has to be signed.
 * So the decision is made here, with the same inputs the api uses, and stated.
 *
 * Twin of api/useCases/admin/updateMissionDetails.usecase.js and
 * api/useCases/admin/patchMissionContract.usecase.js. Only the period is not
 * decided here: it has its own flow (prolongation, interruption).
 */

export const CONTRACT_STATE = {
  NONE: 'none',
  GENERATED: 'generated',
  SENT: 'sent',
  SIGNED: 'signed',
};

/**
 * The fields the BeSTT contract carries and a patch can therefore change. The
 * dress code, the contact, the tool providers and the tasks are not among
 * them, by decision: they apply at once, like the information for the agency.
 * The risky position (Poste_A_Risque and the Penibilite_* factors) goes with
 * the EPI.
 */
const PATCHABLE_FIELDS = [
  'reason',
  'address',
  'qualification',
  'tools',
  'riskyPosition',
];

/**
 * What an engaged contract — one party signed, or the DPAE went out — no longer
 * takes by patch (JBWEB-1089, by decision): its motif, address and
 * qualification change by an amendment of value. The EPI and the risky
 * position are still patched. Twin of the api.
 */
const AMENDED_ONCE_ENGAGED = ['reason', 'address', 'qualification'];

/**
 * The planning rides along only while the contract has gone nowhere: nobody
 * re-signs because the hours moved, and a contract already out keeps the hours
 * it was sent with.
 */
const SCHEDULE_FIELD = 'schedule';

const entriesOf = (mission) =>
  [
    ...(mission?.contract_tempworker ?? []),
    ...(mission?.contract_company ?? []),
  ].filter((entry) => entry?.id_bestt);

export const contractStateOf = (mission) => {
  const entries = entriesOf(mission);
  if (entries.length === 0) return CONTRACT_STATE.NONE;
  // The DPAE engages the contract as much as a signature does (JBWEB-1089).
  if (
    entries.some(
      (entry) => Number(entry.status) === 1 || entry.dpae_transmitted
    )
  )
    return CONTRACT_STATE.SIGNED;
  if (entries.some((entry) => entry.yousign_id)) return CONTRACT_STATE.SENT;
  return CONTRACT_STATE.GENERATED;
};

/** The document a patch would land on: the last one generated. */
export const latestGeneratedEntry = (mission) =>
  (mission?.contract_tempworker ?? [])
    .filter((entry) => entry?.id_bestt)
    .reduce(
      (latest, entry) =>
        (entry.avenant_number || 0) >= (latest?.avenant_number || 0)
          ? entry
          : latest,
      undefined
    );

/**
 * What the patch costs in signatures — the one thing the three contract states
 * differ by. A request already out carries the old text, so it is replaced; a
 * signed contract stays valid and is not sent again.
 */
const isSignedByAParty = (mission) =>
  entriesOf(mission).some((entry) => Number(entry.status) === 1);

const signatureClause = (state, mission) => {
  if (state === CONTRACT_STATE.SIGNED)
    return isSignedByAParty(mission)
      ? "Le contrat signé reste valable : rien n'est renvoyé en signature."
      : "La DPAE est partie : le contrat est engagé, rien n'est renvoyé en signature.";
  if (state === CONTRACT_STATE.SENT)
    return 'Une nouvelle demande de signature remplacera celle en cours.';
  return "Aucune signature n'est envoyée.";
};

/**
 * How many times the term has been renewed.
 *
 * The cap of two is on **renewals** — article L1251-35, « le contrat de mission
 * est renouvelable deux fois » — not on amendments in general. An amendment at
 * constant dates (`V`: the rate, the motif, the address) renews nothing and does
 * not consume one.
 *
 * An amendment carrying no type counts as a renewal: everything written before
 * the type existed was a prolongation, and over-counting only refuses a
 * modification, where under-counting would create an illegal third renewal.
 */
export const prolongationCount = (mission) =>
  (mission?.contract_tempworker ?? []).filter(
    (entry) => (entry.avenant_number || 0) > 0 && entry.contract_type !== 'V'
  ).length;

/**
 * Whether the initial contract is the only document generated — amendments
 * share its BeSTT number, so they count.
 *
 * Its dates can then be patched while nothing is signed: nothing else has to
 * stay in step with them. A value amendment repeats the dates of the document
 * it follows, so with one in place moving the end would break that rule, and
 * the extension goes through a prolongation instead. Twin of the api.
 */
export const isOnlyTheInitialContract = (mission) => {
  const generated = (mission?.contract_tempworker ?? []).filter(
    (entry) => entry?.id_bestt
  );
  return generated.length === 1 && !((generated[0].avenant_number || 0) > 0);
};

/** What extending an unsigned initial contract does: its dates are patched. */
export const describeDatePatch = (mission) =>
  `Rien n'a encore été signé : le contrat BeSTT sera mis à jour avec les nouvelles dates. ${signatureClause(contractStateOf(mission), mission)}`;

/**
 * What moving the start of an engaged contract does (JBWEB-1089, by decision):
 * an amendment of value carrying the new period, from the new start to the
 * mission's end. Nothing is renewed, so it is no prolongation and does not
 * count towards the two. Numbered as the api numbers it: after the highest
 * amendment, generated or not.
 */
export const describeStartChange = (mission, newStartLabel) => {
  const next =
    Math.max(
      0,
      ...(mission?.contract_tempworker ?? []).map(
        (entry) => entry.avenant_number || 0
      )
    ) + 1;
  return `La date de début change : un avenant de valeur (n° ${next}) sera créé, du ${newStartLabel} à la fin de la mission. Ce n'est pas une prolongation : il ne compte pas dans les deux autorisées.`;
};

const isPatchable = (field, { withSchedule }) =>
  PATCHABLE_FIELDS.includes(field) || (withSchedule && field === SCHEDULE_FIELD);

/**
 * @param mission the mission as the detail page holds it, including
 *        `has_imported_bestt_hours`
 * @param changes the pending changes, period excluded
 * @returns the facts, and the sentence that states them
 */
export const describeContractOutcome = ({ mission, changes = [] }) => {
  const state = contractStateOf(mission);
  const fields = changes.map((change) => change.field);
  const contractualFields = changes
    .filter(isContractImpactingChange)
    .map((change) => change.field);

  if (state === CONTRACT_STATE.NONE) {
    return { state, patch: null, valueAmendment: null, message: null };
  }

  const hasImportedHours = Boolean(mission?.has_imported_bestt_hours);
  const entry = latestGeneratedEntry(mission);
  const avenantNumber = entry?.avenant_number || 0;
  const nextAvenantNumber = avenantNumber + 1;

  // Hours already on BeSTT settle it: modifying the contract then loses the
  // consistency with the pay and the invoice to come, so everything becomes an
  // amendment — the rate included — and nothing is ever patched.
  if (hasImportedHours) {
    if (contractualFields.length === 0) {
      return { state, patch: null, valueAmendment: null, message: null };
    }
    return {
      state,
      patch: null,
      valueAmendment: { avenantNumber: nextAvenantNumber },
      message: `Des heures ont déjà été importées sur BeSTT : un avenant de valeur (n° ${nextAvenantNumber}) sera créé avec vos modifications. Le contrat en place n'est pas modifié.`,
    };
  }

  // An engaged contract takes a new motif, address or qualification by an
  // amendment of value — built from the mission, so it carries every change at
  // once, the EPI included. Twin of the api.
  if (
    state === CONTRACT_STATE.SIGNED &&
    contractualFields.some((field) => AMENDED_ONCE_ENGAGED.includes(field))
  ) {
    const why = isSignedByAParty(mission)
      ? 'Le contrat est signé'
      : 'La DPAE est partie';
    return {
      state,
      patch: null,
      valueAmendment: { avenantNumber: nextAvenantNumber },
      message: `${why} : un avenant de valeur (n° ${nextAvenantNumber}) sera créé avec vos modifications. Le contrat en place n'est pas modifié.`,
    };
  }

  const raisesRate = contractualFields.includes('salary');
  const withSchedule = state === CONTRACT_STATE.GENERATED;
  const patchedFields = fields.filter((field) =>
    isPatchable(field, { withSchedule })
  );

  const valueAmendment = raisesRate ? { avenantNumber: nextAvenantNumber } : null;
  const patch = patchedFields.length
    ? { avenantNumber, fields: patchedFields }
    : null;

  if (!patch && !valueAmendment) {
    return {
      state,
      patch: null,
      valueAmendment: null,
      // The permis is the case: it is on the mission and on nothing BeSTT holds.
      message: contractualFields.length
        ? "Le contrat BeSTT ne porte aucun des champs modifiés : rien n'y sera envoyé."
        : null,
    };
  }

  const target =
    avenantNumber > 0 ? `L'avenant n° ${avenantNumber}` : 'Le contrat BeSTT';

  if (patch && valueAmendment) {
    return {
      state,
      patch,
      valueAmendment,
      message: `${target} sera mis à jour, et un avenant de valeur (n° ${nextAvenantNumber}) sera créé pour le nouveau taux. ${signatureClause(state, mission)}`,
    };
  }

  if (valueAmendment) {
    return {
      state,
      patch: null,
      valueAmendment,
      message: `Un avenant de valeur (n° ${nextAvenantNumber}) sera créé avec le nouveau taux. ${target} reste en place.`,
    };
  }

  return {
    state,
    patch,
    valueAmendment: null,
    message: `${target} sera mis à jour. ${signatureClause(state, mission)}`,
  };
};
